import { FC } from "preact/compat";
import { useMemo } from "preact/hooks";
import { useApp } from "~/core";
import { RecordProps } from "./interfaces";
import AccessibilityIcon from "@material-design-icons/svg/outlined/accessibility_new.svg";
import AssignmentIcon from "@material-design-icons/svg/outlined/assignment.svg";
import CalculateIcon from "@material-design-icons/svg/outlined/calculate.svg";
import CalendarIcon from "@material-design-icons/svg/outlined/calendar_month.svg";
import DescriptionIcon from "@material-design-icons/svg/outlined/description.svg";
import DraftIcon from "@material-design-icons/svg/outlined/drafts.svg";
import FileCopyIcon from "@material-design-icons/svg/outlined/file_copy.svg";
import GridIcon from "@material-design-icons/svg/outlined/grid_view.svg";
import LayersIcon from "@material-design-icons/svg/outlined/layers.svg";
import ReceiptIcon from "@material-design-icons/svg/outlined/receipt_long.svg";
import ScheduleIcon from "@material-design-icons/svg/outlined/schedule.svg";
import TaskAltIcon from "@material-design-icons/svg/outlined/task_alt.svg";
import KanbanIcon from "@material-design-icons/svg/outlined/view_kanban.svg";
import DashboardIcon from "~/assets/menu/dashboard.svg";
import MediaIcon from "~/assets/menu/media.svg";
import ProfileIcon from "~/assets/menu/profile.svg";
import cn from "classnames";

// @LINK @DEP routing dependency
const pathKeyRegex = /\/me\/\w+\/\w+\/([a-zA-Z0-9\-_/]*)(\?.*)?/;
const projectKeyRegex = /projects\/.+\/(.*)/;

const PATH_ICONS = [
    {
        s: "dashboard",
        i: DashboardIcon,
    },
    {
        s: "admin/time",
        i: ScheduleIcon,
    },
    {
        s: "admin/approval",
        i: TaskAltIcon,
    },
    {
        s: "admin/expenses",
        i: ReceiptIcon,
    },
    {
        s: "admin/tasks",
        i: AssignmentIcon,
    },
    {
        s: "projects",
        i: MediaIcon,
    },
    {
        s: "resources",
        i: CalendarIcon,
    },
    {
        s: "resources/planning",
        i: CalendarIcon,
    },
    {
        s: "resources/conflicts",
        i: LayersIcon,
    },
    {
        s: "resources/capacity",
        i: AccessibilityIcon,
    },
    {
        s: "profile",
        i: ProfileIcon,
    },
    {
        r: (path: string) => {
            const [, key] = path.match(projectKeyRegex) ?? [];

            if (typeof key === "undefined") {
                return false;
            }

            switch (key) {
                case "budget":
                    return CalculateIcon;
                case "tasks":
                    return KanbanIcon;
                case "purchase":
                    return DraftIcon;
                case "invoicing":
                    return ReceiptIcon;
                case "documents":
                    return FileCopyIcon;
                default:
                    return GridIcon;
            }
        },
    },
];

const Record: FC<RecordProps> = ({ record, index, width, active }) => {
    const { help } = useApp();
    const Icon = useMemo(() => {
        const [, pathKey] = record.path.match(pathKeyRegex) ?? [];

        for (const item of PATH_ICONS) {
            if (item.s) {
                if (item.s === pathKey) {
                    return item.i;
                }
            } else if (item.r) {
                const res = item.r(record.path);

                if (res) {
                    return res;
                }
            }
        }

        return DescriptionIcon;
    }, [record]);
    const tag = help.tags.value.find((t) => t.tag === record.tag);

    return (
        <button
            type="button"
            title={active ? "" : "Go to this manual"}
            disabled={active}
            style={{ width }}
            onClick={() => help.navigateHistoryTo(index)}
            className={cn(
                "flex flex-col gap-y-2 rounded-3xl border border-gray-300 bg-white p-4 shadow-lg transition duration-200",
                !active &&
                    "scale-90 opacity-80 transition duration-100 hover:opacity-100",
                "focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white",
            )}
        >
            <h3
                title={active ? tag?.name : undefined}
                className="max-w-full truncate text-lg font-bold"
            >
                {tag?.name ?? "<unknown>"}
            </h3>
            <div className="flex items-center gap-x-2 text-sm text-gray-500">
                <Icon width="20" height="20" className="fill-current" />
                <span>{record.title}</span>
            </div>
        </button>
    );
};

export default Record;
